import {
  loadRuntimeConfig,
} from "./config/runtimeConfig";

export async function bootstrapApplication(
  {
    loadRuntimeConfiguration =
      loadRuntimeConfig,
    loadApplicationModules =
      importApplicationModules,
    documentObject = document,
    locationObject =
      window.location,
    reloadApplication = () =>
      window.location.reload(),
    logger = console,
  } = {}
) {
  try {
    await loadRuntimeConfiguration();

    const modules =
      await loadApplicationModules();

    renderApplication({
      documentObject,
      locationObject,
      modules,
    });

    return true;
  } catch (error) {
    logger.error(
      "Application bootstrap failed",
      error
    );

    renderBootstrapFailure({
      documentObject,
      reloadApplication,
    });

    return false;
  }
}

export function createRestoreOriginalUri(
  locationObject
) {
  return async (
    _oktaAuth,
    originalUri
  ) => {
    locationObject.replace(
      originalUri ||
        locationObject.origin
    );
  };
}

async function importApplicationModules() {
  const [
    reactModule,
    reactDomClientModule,
    routerModule,
    oktaReactModule,
    appModule,
    oktaAuthModule,
  ] = await Promise.all([
    import("react"),
    import("react-dom/client"),
    import("react-router-dom"),
    import("@okta/okta-react"),
    import("./App"),
    import("./auth/oktaAuth"),
  ]);

  return {
    React:
      reactModule.default ||
      reactModule,
    ReactDOM:
      reactDomClientModule.default ||
      reactDomClientModule,
    BrowserRouter:
      routerModule.BrowserRouter,
    Security:
      oktaReactModule.Security,
    App: appModule.default,
    isLocalAuth:
      oktaAuthModule.isLocalAuth,
    oktaAuth:
      oktaAuthModule.oktaAuth,
  };
}

function renderApplication({
  documentObject,
  locationObject,
  modules,
}) {
  const {
    React,
    ReactDOM,
    BrowserRouter,
    Security,
    App,
    isLocalAuth,
    oktaAuth,
  } = modules;

  const rootElement =
    documentObject.getElementById(
      "root"
    );

  if (!rootElement) {
    throw new Error(
      "Application root element was not found"
    );
  }

  const routedApplication =
    React.createElement(
      BrowserRouter,
      null,
      React.createElement(App)
    );

  const authenticatedApplication =
    isLocalAuth
      ? routedApplication
      : React.createElement(
          Security,
          {
            oktaAuth,
            restoreOriginalUri:
              createRestoreOriginalUri(
                locationObject
              ),
          },
          routedApplication
        );

  const application =
    React.createElement(
      React.StrictMode,
      null,
      authenticatedApplication
    );

  ReactDOM.createRoot(
    rootElement
  ).render(application);
}

function renderBootstrapFailure({
  documentObject,
  reloadApplication,
}) {
  const rootElement =
    documentObject.getElementById(
      "root"
    );

  if (!rootElement) {
    return;
  }

  const failurePanel =
    documentObject.createElement(
      "main"
    );
  failurePanel.setAttribute(
    "role",
    "alert"
  );
  failurePanel.style.cssText = [
    "align-items:center",
    "background:#f5f7fb",
    "box-sizing:border-box",
    "color:#172033",
    "display:flex",
    "font-family:-apple-system,BlinkMacSystemFont,\"Segoe UI\",sans-serif",
    "justify-content:center",
    "min-height:100vh",
    "padding:2rem",
  ].join(";");

  const messageCard =
    documentObject.createElement(
      "section"
    );
  messageCard.style.cssText = [
    "background:#ffffff",
    "border:1px solid #dce2ec",
    "border-radius:16px",
    "box-shadow:0 12px 32px rgba(23,32,51,0.12)",
    "max-width:32rem",
    "padding:2rem",
    "text-align:center",
    "width:100%",
  ].join(";");

  const heading =
    documentObject.createElement("h1");
  heading.textContent =
    "Dispatch Manager unavailable";
  heading.style.cssText = [
    "font-size:1.5rem",
    "margin:0 0 0.75rem",
  ].join(";");

  const message =
    documentObject.createElement("p");
  message.textContent =
    "The application could not load its runtime configuration.";
  message.style.cssText = [
    "line-height:1.5",
    "margin:0 0 0.75rem",
  ].join(";");

  const guidance =
    documentObject.createElement("p");
  guidance.textContent =
    "Check the deployment configuration, then try again.";
  guidance.style.cssText = [
    "color:#596579",
    "line-height:1.5",
    "margin:0 0 1.5rem",
  ].join(";");

  const retryButton =
    documentObject.createElement(
      "button"
    );
  retryButton.type = "button";
  retryButton.textContent = "Retry";
  retryButton.style.cssText = [
    "background:#1f5eff",
    "border:0",
    "border-radius:8px",
    "color:#ffffff",
    "cursor:pointer",
    "font:inherit",
    "font-weight:600",
    "padding:0.7rem 1.25rem",
  ].join(";");
  retryButton.addEventListener(
    "click",
    reloadApplication
  );

  messageCard.append(
    heading,
    message,
    guidance,
    retryButton
  );
  failurePanel.appendChild(
    messageCard
  );
  rootElement.replaceChildren(
    failurePanel
  );
}
